import React, { useState, useRef, useEffect } from 'react'
import  {IconButton, Button} from '../design-system/index'  
import { CalendarLayout } from '../design-system/components/Layout/CalendarLayout'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CalendarContainer, CalendarHeader } from '../design-system/components/Layout/CalendarLayout';
import CalendarCard from '../components/calendar/CalendarCardV2';
import TaskList from '../components/calendar/TaskList';
import TaskModal from '../components/TaskModal';
import { useTasks } from '../context/TaskContext';
import { useToast } from '../hooks/use-toast';
import { useNotes } from '../context/NoteContext';
import {
    fetchAllDayTasks,
    getTaskStyles,
    getTimeSlots,
    calculateTaskPosition,
    assignTasksToColumns,
    handleNoteClick,
    handleToggleTaskCompletion,
    handleDeleteTask,
    updateSidebarTasks,
  } from "../utils/calendarUtils";
import taskService from "../services/taskService";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Check,
  PlusCircle,
  Trash2,
  Menu,
  X,
} from "lucide-react";
import { FaStickyNote } from "react-icons/fa";
import TextBox, { futuristicInputStyles } from '../components/calendar/TextBox';
import { format, startOfDay, addDays, isSameDay, parseISO } from "date-fns";
import '../components/calendar/calendar-styles-v2.css'


export default function CalendarScreenV3() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [darkMode, setDarkMode] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tasksByDay, setTasksByDay] = useState({});
  const [calendarDays, setCalendarDays] = useState([]);
  const [activeTab, setActiveTab] = useState("ThisWeek");
  const [currentTask, setCurrentTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputTaskString, setInputTaskString] = useState("");
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);
  const [currentDate, setCurrentDate] = useState(startOfDay(new Date()));
  const [tasks, setTasks] = useState([]);
  const [dropTarget, setDropTarget] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedTaskBubble, setExpandedTaskBubble] = useState(null);
  const [currentView, setCurrentView] = useState("threeweekdays");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const taskListRef = useRef(null);
  const dropProvided = useRef(null);
  const { deleteTask } = useTasks();
  const { createNote } = useNotes();
  const { toast } = useToast();


  // Navigation items matching the image
  const navigationItems = [
    { id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' },
    { id: 'meetings', label: 'Meetings', icon: 'fa-solid fa-users' },
    { id: 'notes', label: 'Notes', icon: 'fa-solid fa-sticky-note' },
    { id: 'tasks', label: 'Tasks', icon: 'fa-solid fa-list-check' },
    { id: 'categories', label: 'Categories', icon: 'fa-solid fa-layer-group' },
  ];

  // Debug useEffect to watch state changes
  useEffect(() => {
    // //console.log was removed here
  }, [isModalOpen, currentTask]);

  // Initialize dark mode on component mount
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedDarkMode === 'true' || (savedDarkMode === null && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Initialize calendar days on component mount
  useEffect(() => {
    // Initialize with current view based on screen size
    const today = new Date();
    
    // Check for mobile first to set appropriate view
    const checkMobile = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);
      
      // Set up initial view based on detected screen size
      if (isMobile) {
        // Two day view for mobile - today and tomorrow
        const days = [
          today.toISOString().split('T')[0],
          new Date(today.getTime() + 86400000).toISOString().split('T')[0] // add 1 day in milliseconds
        ];
        setCalendarDays(days);
      } else {
        // 3-day view for desktop
        const startOfRange = new Date(today);
        startOfRange.setDate(today.getDate() - 1); // Start with yesterday
        
        const days = [];
        // Get 3 days for the initial view
        for (let i = 0; i < 3; i++) {
          const day = new Date(startOfRange);
          day.setDate(startOfRange.getDate() + i);
          days.push(day.toISOString().split('T')[0]);
        }
        
        setCalendarDays(days);
      }
    };
    
    // Run the check immediately
    checkMobile();
    
    // Set up event listener for resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', checkMobile);
  }, []); // Empty dependency array ensures this only runs once on mount

  useEffect(() => {
    taskService.getTasks({ taskType: activeTab }).then((response) => {
      setTasks(response.tasks || []);
    });
  }, [activeTab]);

  // useEffect to fetch tasks for the calendar days when they change
  useEffect(() => {
    // Only fetch tasks if calendarDays has been initialized
    if (calendarDays && calendarDays.length > 0) {
      handleFetchTasks(calendarDays);
    }
  }, [calendarDays]);

  const handleNavigation = (item) => {
    switch (item.id) {
      case 'analytics':
        navigate('/dashboard');
        break;
      case 'meetings':
        navigate('/calendar');
        break;
      case 'notes':
        navigate('/notes');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'categories':
        // If categories page doesn't exist, show a message or navigate to dashboard
        navigate('/tasks'); // For now, redirect to tasks
        break;
      default:
        navigate('/dashboard');
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  const toggleSidebar = () => {
    console.log('toggleSidebar', sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  // Toggle sidebar visibility for mobile view
  const toggleMobileSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Toggle sidebar collapsed state for desktop view
  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Function to handle task input submission
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    // Prevent submission if empty or already submitting
    if (!inputTaskString.trim() || isSubmittingTask) return;

    setIsSubmittingTask(true); // Set loading true
    try {
      await taskService.createTaskFromString(inputTaskString);
      setInputTaskString(""); // Clear the input field

      // Refresh tasks in the *currently active* tab after submission
      const response = await taskService.getTasks({ taskType: activeTab });
      setTasks(response.tasks || []);

      // Also refresh the calendar view
      handleFetchTasks(calendarDays);
    } catch (error) {
      console.error("Error creating task:", error);
      // Consider adding user feedback for the error here
    } finally {
      setIsSubmittingTask(false); // Set loading false regardless of outcome
    }
  };

  const goToToday = () => {
    setCurrentDate(startOfDay(new Date()));
  };

  const navigateDays = (direction) => {
    const newDate =
      direction === "next"
        ? addDays(currentDate, isMobileView ? 2 : 3)
        : addDays(currentDate, isMobileView ? -2 : -3);
    setCurrentDate(newDate);
  };

  // Function to handle view change and auto-collapse sidebar for wider views
  const handleViewChange = (view) => {
    setCurrentView(view);
    // Auto-collapse sidebar for 5-day or week views
    if (view === 'fiveweekdays' || view === 'week') {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarCollapsed(false);
    }
  };

  const handleEditTask = (task, event) => {
    // Prevent event propagation so it doesn't trigger drag operations
    if (event) {
      event.stopPropagation();
    }
    setCurrentTask(task);
    setIsModalOpen(true);
  };

  // Handle save task
  const handleSaveTask = () => {
    // Refresh all tasks after a task is saved
    handleFetchTasks(calendarDays);
    // Also refresh sidebar tasks if needed
    const fetchTasks = async () => {
      try {
        const response = await taskService.getTasks({ taskType: activeTab });
        setTasks(response.tasks || []);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };
    fetchTasks();
  };

  // Function to update task scheduling when a task is dropped on calendar
  const updateTaskScheduling = async (taskId, date, startTime, endTime, title = "New Task") => {
    try {
      // Format the date for the API
      const dateString = format(date, 'yyyy-MM-dd');
      
      // If taskId is null, we're creating a new task via click-and-drag
      if (taskId === null) {
        // Format start and end times with date for ISO format
        const isoStartTime = startTime ? `${dateString}T${startTime}:00` : null;
        const isoEndTime = endTime ? `${dateString}T${endTime}:00` : null;
        
        // Create new task data
        const newTaskData = {
          title: title,
          scheduled_date: dateString,
          start_time: isoStartTime,
          end_time: isoEndTime,
          task_type: 'Scheduled',
          is_high_priority: false,
          is_completed: false
        };
        
        try {
          // Create the new task
          const createdTask = await taskService.createTask(newTaskData);
          
          if (!createdTask || !createdTask.id) {
            throw new Error('Invalid task created');
          }
          
          // Refresh calendar view
          handleFetchTasks(calendarDays);
          
          // Also refresh sidebar tasks
          const response = await taskService.getTasks({ taskType: activeTab });
          setTasks(response.tasks || []);
          
          // Show success message with edit button
          toast({
            title: "Task created successfully",
            description: (
              <div>
                <div>Scheduled for {format(date, 'MMM dd')} at {startTime}</div>
                <button 
                  onClick={() => {
                    setCurrentTask(createdTask);
                    setIsModalOpen(true);
                  }}
                  className="mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                >
                  Edit Task
                </button>
              </div>
            ),
            variant: "default",
            duration: 5000, // Show for longer
          });
          
          // Retrieve the full task data to ensure all properties are present
          const fullTaskData = await taskService.getTaskById(createdTask.id);
          
          // Open task edit modal for the new task
          setCurrentTask(fullTaskData);
          setIsModalOpen(true);
        } catch (error) {
          console.error("Error creating new task:", error);
          toast({
            title: "Failed to create task",
            variant: "destructive",
          });
        }
        
        return;
      }
      
      // Find the task that's being moved (existing code for moving tasks)
      let task = tasks.find(t => String(t.id) === String(taskId));
      
      // If not found in taskList, check in the calendar days
      if (!task) {
        // Search in all days' tasks
        for (const day of Object.values(tasksByDay)) {
          const foundTask = day.find(t => String(t.id) === String(taskId));
          if (foundTask) {
            task = foundTask;
            break;
          }
        }
      }
      
      if (!task) {
        console.error(`Task not found with id: ${taskId}`);
        return;
      }
      
      // Create end time 1 hour after start time if not provided
      let calculatedEndTime = endTime;
      if (!calculatedEndTime && startTime) {
        const [hours, minutes] = startTime.split(':').map(num => parseInt(num, 10));
        const endHour = (hours + 1) % 24;
        calculatedEndTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // Format start time with date for ISO format
      const isoStartTime = startTime ? `${dateString}T${startTime}:00` : null;
      // Format end time with date for ISO format
      const isoEndTime = calculatedEndTime ? `${dateString}T${calculatedEndTime}:00` : null;
      
      // Match the format used in CalendarView's handleDragEnd function
      const updatedTask = await taskService.updateTask(task.id, {
        ...task,                    // Include all original task properties
        scheduled_date: dateString, // Override scheduling-related fields
        start_time: isoStartTime,
        end_time: isoEndTime,
        task_type: 'Scheduled',
        // Explicitly preserve the category
        category_id: task.category?.id || null,
        // Use category and category_id to ensure compatibility with all API endpoints
        category: task.category || null,
        // Explicitly preserve the notes_link
        notes_link: task.notes_link || null
      });
      
      // Refresh calendar view
      handleFetchTasks(calendarDays);
      
      // Also refresh sidebar tasks
      const response = await taskService.getTasks({ taskType: activeTab });
      setTasks(response.tasks || []);
      
      toast({
        title: "Task scheduled successfully",
        description: startTime 
          ? `Scheduled for ${format(date, 'MMM dd')} at ${startTime}` 
          : `Scheduled for ${format(date, 'MMM dd')}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error scheduling task:", error);
      toast({
        title: "Failed to schedule task",
        variant: "destructive",
      });
    }
  };

  const handleFetchTasks = (days) => {
    fetchAllDayTasks(days, setTasksByDay);
  };

  // Helper function to darken a hex color
  const darkenColor = (color, percent = 20) => {
    if (!color) return color;
    
    // Remove # if present
    const hex = color.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken by reducing each component
    const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    
    // Convert back to hex
    const toHex = (n) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
  };

  const renderTask = (
    task,
    index,
    isDragging,
    taskSize = { type: "normal" }
  ) => {
    let startTime = "";
    let endTime = "";

    // Check if this is an ongoing task (has start_time but no end_time)
    const isOngoingTask = task.start_time && !task.end_time;

    // Get styles based on task size
    const styles = getTaskStyles(taskSize);

    // Parse start time
    if (task.start_time) {
      try {
        if (task.start_time.includes("T")) {
          // ISO format
          const date = new Date(task.start_time);
          startTime = `${date.getUTCHours().toString().padStart(2, "0")}:${date
            .getUTCMinutes()
            .toString()
            .padStart(2, "0")}`;
        } else {
          // Simple time format
          startTime = task.start_time.split(":").slice(0, 2).join(":");
        }
      } catch (e) {
        console.error("Error parsing start time:", e);
      }
    }

    // Parse end time
    if (task.end_time) {
      try {
        if (task.end_time.includes("T")) {
          // ISO format
          const date = new Date(task.end_time);
          endTime = `${date.getUTCHours().toString().padStart(2, "0")}:${date
            .getUTCMinutes()
            .toString()
            .padStart(2, "0")}`;
        } else {
          // Simple time format
          endTime = task.end_time.split(":").slice(0, 2).join(":");
        }
      } catch (e) {
        console.error("Error parsing end time:", e);
      }
    }

    const timeDisplay =
      startTime && endTime ? `${startTime} - ${endTime}` : startTime || "";

    // For task list rendering (not in calendar)
    if (index >= 0) {
      // Return a simplified version for the task list
      return (
        <div
          className={`p-3 rounded-md shadow-sm bg-white border-l-4 ${
            task.is_completed
              ? "border-green-500"
              : task.is_high_priority
              ? "border-red-500"
              : task.category?.color
              ? ""
              : "border-blue-500"
          }`}
          style={{
            ...(task.category?.color
              ? { borderLeftColor: task.category.color }
              : {}),
            ...(isOngoingTask
              ? {
                  borderStyle: "dashed",
                  borderColor: "#10b981",
                  borderWidth: "1px",
                }
              : {}),
            cursor: "grab"
          }}
          onClick={(e) => handleEditTask(task, e)}
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData('application/json', JSON.stringify({
              taskId: task.id,
              task: task,
              fromList: true
            }));
            e.dataTransfer.effectAllowed = 'move';
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <h3
                  className={`text-sm font-medium truncate ${
                    task.is_completed ? "text-gray-500 line-through" : ""
                  }`}
                  title={task.title}
                >
                  {task.title}
                </h3>
                {isOngoingTask && (
                  <span className="ml-1 flex items-center text-[10px] text-green-600 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse mr-1"></span>
                    Ongoing
                  </span>
                )}
              </div>
              {timeDisplay && (
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {timeDisplay}
                </p>
              )}
            </div>
            <div className="flex items-center">
              <button
                onClick={(e) => handleNoteClick(task, e)}
                className={`p-1.5 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-50 flex-shrink-0 mr-2`}
                title={
                  task.notes_link ? "Open connected note" : "Create new note"
                }
              >
                <FaStickyNote
                  className={`h-4 w-4 ${
                    task.notes_link ? "text-blue-500" : "text-gray-400"
                  }`}
                />
              </button>
              <button
                onClick={(e) => handleDeleteTask(task.id, e, handleFetchTasks, calendarDays, activeTab, setTasks, deleteTask, toast)}
                className="p-1.5 rounded-full hover:bg-gray-100 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-50 flex-shrink-0 mr-2"
                title="Delete task"
              >
                <Trash2 className="h-4 w-4 text-gray-400" />
              </button>
              <button
                onClick={(e) => handleToggleTaskCompletion(task.id, e)}
                className={`p-1.5 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  task.is_completed ? "bg-green-100" : "bg-gray-50"
                } flex-shrink-0 ml-2`}
              >
                <Check
                  className={`h-4 w-4 ${
                    task.is_completed ? "text-green-500" : "text-gray-400"
                  }`}
                />
              </button>
            </div>
          </div>
          {task.category && (
            <div className="mt-2">
              <span
                className="text-xs px-2 py-0.5 rounded-full truncate inline-block max-w-full"
                style={{
                  backgroundColor: `${task.category.color}20`,
                  color: task.category.color,
                }}
              >
                {task.category.name}
              </span>
            </div>
          )}
        </div>
      );
    }

    // For calendar rendering
    // Determine task background color based on category or priority
    let bgColor = ""; // Base background is handled by inline styles
    let completedStyle = task.is_completed ? "opacity-90" : ""; // Keep opacity adjustment
    // Ongoing style border is handled by inline styles
    let ongoingStyle = ""; // Remove this

    // Use category color for border only if not high priority

    // const dynamicBorderColor = task.is_completed
    //   ? "#10b981" // Completed green
    //   : task.is_high_priority
    //   ? "#ef4444" // High priority red
    //   : task.category?.color || "#3b82f6"; // Category or default blue

    const dynamicBorderColor =  task.category?.color || "#3b82f6"

    let dynamicBackgroundColor = task.is_completed
      ? "rgba(240, 253, 244, 0.85)" // Completed background
      : task.is_high_priority && !task.is_completed
      ? "rgba(254, 226, 226, 0.85)" // High priority background (if not completed)
      : "rgba(255, 255, 255, 0.85)"; // Default background

    if (darkMode) {
      dynamicBackgroundColor = task.category?.color ? darkenColor(task.category.color, 50) : darkenColor("#3b82f6", 30);
    }
    console.log("dynamicBackgroundColor", dynamicBackgroundColor)
    // Main Task Div
    return (
      <div
        className={`${styles.padding} rounded-md shadow h-full ${bgColor} ${
          isDragging ? "opacity-50" : ""
        } ${completedStyle} overflow-hidden hover:shadow-md transition-shadow duration-200 backdrop-blur-sm`}
        style={{
          textDecoration: task.is_completed ? "line-through" : "none",
          backgroundColor: dynamicBackgroundColor, // Use calculated background
          backdropFilter: "blur(8px)",
          cursor: "grab",
          borderWidth: "1px", // Consistent border width
          borderStyle: isOngoingTask ? "dashed" : "solid", // Dynamic border style
          borderColor: isOngoingTask ? "#10b981" : "#e5e7eb", // Dynamic border color for top/right/bottom
          borderLeftWidth: isOngoingTask?"1px": "5px", // Consistent left border width
          borderLeftColor: isOngoingTask? "#10b981" : dynamicBorderColor, // Dynamic left border color
          height: "100%",
          boxSizing: "border-box",
          maxHeight: "100%",
          overflow: "hidden",
          zIndex: 5, // Keep z-index
        }}
        onClick={(e) => handleEditTask(task, e)}
        draggable={true}
        onDragStart={(e) => {
          e.dataTransfer.setData('application/json', JSON.stringify({
            taskId: task.id,
            task: task,
            fromCalendar: true
          }));
          e.dataTransfer.effectAllowed = 'move';
        }}
      >
        <div className="flex flex-col h-full">
          <div
            className={`flex items-center ${
              styles.centerContent ? "justify-between" : "justify-between"
            }`}
          >
            <div className="flex-1 min-w-0 overflow-hidden text-left">
              <div className="flex items-center text-left">
                <h3
                  className={`${
                    styles.titleFontSize
                  } font-medium truncate text-left ${
                    task.is_completed ? "text-gray-500" : ""
                  }`}
                  title={task.title}
                >
                  {task.title}
                </h3>
                {isOngoingTask && styles.showOngoingIndicator && (
                  <span className="ml-1 flex items-center text-[8px] text-green-600 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse mr-0.5"></span>
                    <span className="truncate">Ongoing</span>
                  </span>
                )}
              </div>
              {styles.showTime && timeDisplay && (
                <p
                  className={`${styles.timeFontSize} text-gray-500 truncate text-left`}
                >
                  {timeDisplay}
                </p>
              )}
            </div>

            {taskSize.type === "small" || taskSize.type === "extraSmall" ? (
              <div className="flex items-center">
                {styles.showCheckbox && task.notes_link !== undefined && (
                  <button
                    onClick={(e) => handleNoteClick(task, e, createNote)}
                    className={`${
                      styles.buttonPadding
                    } rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      task.notes_link ? "bg-blue-100" : "bg-gray-50"
                    } flex-shrink-0 ${styles.buttonMargin} mr-1`}
                    title={
                      task.notes_link
                        ? "Open connected note"
                        : "Create new note"
                    }
                  >
                    <FaStickyNote
                      className={`${styles.checkIconSize} ${
                        task.notes_link ? "text-blue-500" : "text-gray-400"
                      }`}
                    />
                  </button>
                )}
                {styles.showCheckbox && (
                  <button
                    onClick={(e) => handleDeleteTask(task.id, e, handleFetchTasks, calendarDays, activeTab, setTasks, deleteTask, toast)}
                    className={`${styles.buttonPadding} rounded-full hover:bg-gray-100 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-50 flex-shrink-0 ${styles.buttonMargin} mr-1`}
                    title="Delete task"
                  >
                    <Trash2
                      className={`${styles.checkIconSize} text-gray-400`}
                    />
                  </button>
                )}
                {styles.showCheckbox && (
                  <button
                    onClick={(e) => handleToggleTaskCompletion(task.id, e, handleFetchTasks, calendarDays, activeTab, setTasks)}
                    className={`${
                      styles.buttonPadding
                    } rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      task.is_completed ? "bg-green-100" : "bg-gray-50"
                    } flex-shrink-0 ${styles.buttonMargin}`}
                  >
                    <Check
                      className={`${styles.checkIconSize} ${
                        task.is_completed ? "text-green-500" : "text-gray-400"
                      }`}
                    />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-1">
                {styles.showCheckbox && (
                  <button
                    onClick={(e) => handleToggleTaskCompletion(task.id, e)}
                    className={`${
                      styles.buttonPadding
                    } rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      task.is_completed ? "bg-green-100" : "bg-gray-50"
                    } flex-shrink-0 ${styles.buttonMargin}`}
                  >
                    <Check
                      className={`${styles.checkIconSize} ${
                        task.is_completed ? "text-green-500" : "text-gray-400"
                      }`}
                    />
                  </button>
                )}
                {styles.showCheckbox && (
                  <button
                    onClick={(e) => handleDeleteTask(task.id, e, handleFetchTasks, calendarDays, activeTab, setTasks, deleteTask, toast)}
                    className={`${styles.buttonPadding} rounded-full hover:bg-gray-100 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-50 flex-shrink-0 ${styles.buttonMargin}`}
                    title="Delete task"
                  >
                    <Trash2
                      className={`${styles.checkIconSize} text-gray-400`}
                    />
                  </button>
                )}
                {styles.showCheckbox && task.notes_link !== undefined && (
                  <button
                    onClick={(e) => handleNoteClick(task, e)}
                    className={`${
                      styles.buttonPadding
                    } rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      task.notes_link ? "bg-blue-100" : "bg-gray-50"
                    } flex-shrink-0 ${styles.buttonMargin}`}
                    title={
                      task.notes_link
                        ? "Open connected note"
                        : "Create new note"
                    }
                  >
                    <FaStickyNote
                      className={`${styles.checkIconSize} ${
                        task.notes_link ? "text-blue-500" : "text-gray-400"
                      }`}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
          {styles.showCategory && task.category && (
            <div className="mt-auto">
              <span
                className={`${styles.categoryFontSize} px-1 py-0.5 rounded-full truncate inline-block max-w-full`}
                style={{
                  backgroundColor: `${task.category.color}20`,
                  color: task.category.color,
                }}
              >
                {task.category.name}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <CalendarLayout user={user} activeItem="meetings" navigationItems={navigationItems} onNavigate={handleNavigation} sidebarCollapsed={!sidebarOpen}>
      <div className="flex flex-row h-screen">
        {/* Mobile menu toggle button - visible only on mobile */}
        {isMobileView && (
          <button 
            onClick={toggleMobileSidebar}
            className="fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg flex items-center justify-center"
            aria-label="Toggle task list"
          >
            {isSidebarOpen ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600" />
            )}
          </button>
        )}
        
        <CalendarContainer className="flex-1">
          <CalendarHeader 
            title="Smart Calendar" 
            onBack={() => navigate('/')} 
            onMenuToggle={() => toggleSidebar()} 
            sidebarOpen={sidebarOpen} 
            backButton={false} 
            actions={
              <Button
                variant={darkMode ? "accent-gradient" : "outline"}
                onClick={toggleDarkMode}
                leftIcon={<i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />}
                className={darkMode ? "shadow-glow" : ""}
              >
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </Button>
            } 
          />
          
          <div className={`flex flex-1 calendar-container ${!isMobileView && !isSidebarCollapsed ? 'pr-96' : ''}`}>
            <CalendarCard 
              tasksByDay={tasksByDay}
              renderTask={renderTask}
              fetchAllDayTasks={handleFetchTasks}
              calendarDays={calendarDays}
              setCalendarDays={setCalendarDays}
              onTaskDrop={updateTaskScheduling}
              onViewChange={handleViewChange}
            />
          </div>
        </CalendarContainer>
        
        {/* Sidebar toggle button for desktop when collapsed */}
        {!isMobileView && (
          <button
            onClick={toggleSidebarCollapse}
            className={`fixed top-1/2 transform -translate-y-1/2 z-40 h-12 w-6 bg-gray-200 rounded-l-md flex items-center justify-center shadow-md hover:bg-gray-300 transition-colors duration-200 ${
              isSidebarCollapsed ? 'right-0' : 'right-96'
            }`}
            aria-label={isSidebarCollapsed ? "Show task list" : "Hide task list"}
          >
            <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
        )}
        
        {/* Fixed sticky sidebar - doesn't scroll with calendar content */}
        <div 
          className={`${
            isMobileView 
              ? `fixed inset-y-0 right-0 z-40 w-80 transform transition-transform duration-300 ease-in-out ${
                  isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
                }`
              : `fixed top-0 right-0 h-screen z-30 transform transition-all duration-300 ease-in-out ${
                  isSidebarCollapsed ? 'w-0 opacity-0 invisible translate-x-full' : 'w-96 opacity-100 visible translate-x-0'
                } flex-shrink-0`
          } bg-gray-50 dark:bg-dark-100 border-l border-gray-200 dark:border-dark-300 flex flex-col overflow-hidden shadow-lg`}
        >
          {/* Add close button for mobile - visible at the top of the sidebar */}
          {isMobileView && isSidebarOpen && (
            <div className="flex justify-end mb-2 py-2 px-3">
              <button
                onClick={toggleMobileSidebar}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-dark-200 focus:outline-none"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-dark-600" />
              </button>
            </div>
          )}
          
          {/* Sidebar content container with proper spacing */}
          <div className="flex flex-col h-full p-3">
            {/* Task List - scrollable area */}
            <div className="flex-1 overflow-y-auto">
              <TaskList
                tasks={tasks}
                setCurrentTask={setCurrentTask}
                setIsModalOpen={setIsModalOpen}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                renderTask={renderTask}
              />
            </div>

            {/* Text Box - fixed at bottom */}
            <div className="flex-shrink-0 mt-3">
              <TextBox
                inputTaskString={inputTaskString}
                setInputTaskString={setInputTaskString}
                handleTaskSubmit={handleTaskSubmit}
                isSubmittingTask={isSubmittingTask}
                futuristicInputStyles={futuristicInputStyles}
              />
            </div>
          </div>
        </div>
        
        {/* Add overlay backdrop for mobile sidebar */}
        {isMobileView && isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-30"
            onClick={toggleMobileSidebar}
          />
        )}
      </div>

      {/* Task Modal */}
      {isModalOpen && (
        <TaskModal
          task={currentTask}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveTask}
        />
      )}
    </CalendarLayout>
  )
}